import { isSafeHref, zRichText, type RichText } from '@mai-work-report/contracts';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import LinkIcon from '@mui/icons-material/Link';
import RedoIcon from '@mui/icons-material/Redo';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import UndoIcon from '@mui/icons-material/Undo';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useCommonT } from '@mai-team-amusement/ui';
import {
  EditorContent,
  useEditor,
  useEditorState,
  type Editor,
  type JSONContent,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useReportT } from '../i18n/index.js';
import { RICH_TEXT_STYLES } from './RichTextView.js';

/** Height limits of the resizable editing area, in px. */
export const EDITOR_MIN_HEIGHT = 120;
export const EDITOR_MAX_HEIGHT = 640;
const EDITOR_PADDING = 1;
const TOOLBAR_PADDING = 0.5;
const LINK_FIELD_MIN_WIDTH = 360;

/** Only what the server accepts: no headings, quotes, code or rules; safe links only. */
const extensions = [
  StarterKit.configure({
    heading: false,
    blockquote: false,
    codeBlock: false,
    code: false,
    horizontalRule: false,
    link: {
      openOnClick: false,
      autolink: true,
      defaultProtocol: 'https',
      isAllowedUri: (url, context) => context.defaultValidate(url) && isSafeHref(url),
    },
  }),
];

/**
 * `RichText` is a subset of TipTap's `JSONContent`; they differ only in spelling optional fields
 * as `?: T | undefined` (zod's output), which TipTap treats the same as an absent field.
 */
const toEditorContent = (doc: RichText) => doc as JSONContent;

/** `example.com` becomes `https://example.com`; other schemes are kept (and then validated). */
const withProtocol = (address: string) =>
  /^[a-z][a-z0-9+.-]*:/i.test(address) ? address : `https://${address}`;

function ToolbarButton({
  label,
  active = false,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          size="small"
          aria-label={label}
          aria-pressed={active}
          color={active ? 'primary' : 'default'}
          disabled={disabled}
          onMouseDown={(event) => {
            // Keep the editor's selection while clicking the toolbar.
            event.preventDefault();
          }}
          onClick={onClick}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function LinkDialog({ editor, onClose }: { editor: Editor; onClose: () => void }) {
  const t = useReportT();
  const common = useCommonT();
  const current: unknown = editor.getAttributes('link')['href'];
  const [address, setAddress] = useState(typeof current === 'string' ? current : '');
  const [invalid, setInvalid] = useState(false);
  const apply = () => {
    const trimmed = address.trim();
    if (trimmed === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      onClose();
      return;
    }
    const href = withProtocol(trimmed);
    if (!isSafeHref(href)) {
      setInvalid(true);
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    onClose();
  };
  return (
    <Dialog open onClose={onClose}>
      <DialogContent>
        <TextField
          autoFocus
          label={t('editor.linkAddress')}
          value={address}
          error={invalid}
          helperText={invalid ? t('editor.invalidLink') : ' '}
          sx={{ minWidth: { sm: LINK_FIELD_MIN_WIDTH } }}
          onChange={(event) => {
            setAddress(event.target.value);
            setInvalid(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              apply();
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        {typeof current === 'string' ? (
          <Button
            color="error"
            onClick={() => {
              editor.chain().focus().extendMarkRange('link').unsetLink().run();
              onClose();
            }}
          >
            {t('editor.removeLink')}
          </Button>
        ) : null}
        <Button onClick={onClose}>{common('action.cancel')}</Button>
        <Button variant="contained" onClick={apply}>
          {common('action.ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const t = useReportT();
  const [linking, setLinking] = useState(false);
  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      bold: current.isActive('bold'),
      italic: current.isActive('italic'),
      underline: current.isActive('underline'),
      strike: current.isActive('strike'),
      bulletList: current.isActive('bulletList'),
      orderedList: current.isActive('orderedList'),
      link: current.isActive('link'),
      canUndo: current.can().undo(),
      canRedo: current.can().redo(),
    }),
  });
  const chain = () => editor.chain().focus();
  return (
    <Stack
      direction="row"
      role="toolbar"
      sx={{
        flexWrap: 'wrap',
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider',
        px: TOOLBAR_PADDING,
      }}
    >
      <ToolbarButton
        label={t('editor.bold')}
        active={state.bold}
        onClick={() => chain().toggleBold().run()}
      >
        <FormatBoldIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.italic')}
        active={state.italic}
        onClick={() => chain().toggleItalic().run()}
      >
        <FormatItalicIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.underline')}
        active={state.underline}
        onClick={() => chain().toggleUnderline().run()}
      >
        <FormatUnderlinedIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.strike')}
        active={state.strike}
        onClick={() => chain().toggleStrike().run()}
      >
        <StrikethroughSIcon fontSize="small" />
      </ToolbarButton>
      <Divider orientation="vertical" flexItem />
      <ToolbarButton
        label={t('editor.bulletList')}
        active={state.bulletList}
        onClick={() => chain().toggleBulletList().run()}
      >
        <FormatListBulletedIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.orderedList')}
        active={state.orderedList}
        onClick={() => chain().toggleOrderedList().run()}
      >
        <FormatListNumberedIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.link')}
        active={state.link}
        onClick={() => {
          setLinking(true);
        }}
      >
        <LinkIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.clearFormatting')}
        onClick={() => chain().unsetAllMarks().clearNodes().run()}
      >
        <FormatClearIcon fontSize="small" />
      </ToolbarButton>
      <Divider orientation="vertical" flexItem />
      <ToolbarButton
        label={t('editor.undo')}
        disabled={!state.canUndo}
        onClick={() => chain().undo().run()}
      >
        <UndoIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        label={t('editor.redo')}
        disabled={!state.canRedo}
        onClick={() => chain().redo().run()}
      >
        <RedoIcon fontSize="small" />
      </ToolbarButton>
      {linking ? (
        <LinkDialog
          editor={editor}
          onClose={() => {
            setLinking(false);
          }}
        />
      ) : null}
    </Stack>
  );
}

export type RichTextEditorProps = {
  /** Accessible name of the editing area. */
  label: string;
  value: RichText;
  onChange: (value: RichText) => void;
  /** Called once the editor exists (focus control, tests). */
  onReady?: (editor: Editor) => void;
};

/**
 * Rich text with a fixed toolbar, in a vertically resizable area that wraps long lines. Emits
 * only documents that pass the server's allowlist (`zRichText`).
 */
export function RichTextEditor({ label, value, onChange, onReady }: RichTextEditorProps) {
  const lastEmitted = useRef(JSON.stringify(value));
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const editor = useEditor({
    extensions,
    content: toEditorContent(value),
    editorProps: {
      attributes: { 'aria-label': label, 'aria-multiline': 'true', role: 'textbox' },
    },
    onUpdate: ({ editor: current }) => {
      const parsed = zRichText.safeParse(current.getJSON());
      if (parsed.success) {
        lastEmitted.current = JSON.stringify(parsed.data);
        onChangeRef.current(parsed.data);
      }
    },
  });

  useEffect(() => {
    onReady?.(editor);
  }, [editor, onReady]);

  // Replace the content when the value changes from outside (reload, restored draft).
  useEffect(() => {
    const incoming = JSON.stringify(value);
    if (incoming !== lastEmitted.current) {
      lastEmitted.current = incoming;
      editor.commands.setContent(toEditorContent(value), { emitUpdate: false });
    }
  }, [editor, value]);

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Toolbar editor={editor} />
      <Box
        data-testid="rich-text-area"
        sx={{
          resize: 'vertical',
          overflow: 'auto',
          height: EDITOR_MIN_HEIGHT,
          minHeight: EDITOR_MIN_HEIGHT,
          maxHeight: EDITOR_MAX_HEIGHT,
          p: EDITOR_PADDING,
          cursor: 'text',
          '& .ProseMirror': {
            ...RICH_TEXT_STYLES,
            minHeight: '100%',
            outline: 'none',
          },
        }}
        onClick={() => {
          if (!editor.isFocused) {
            editor.commands.focus('end');
          }
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}
